import * as React from 'react';
import {
  Box,
  keyframes,
  ParagraphText,
  StackLayout,
  VisuallyHidden,
} from '@leagueplatform/genesis-core';

const indeterminateAnimation = keyframes({
  '0%': {
    transform:
      'translateX(calc(var(--progress-indeterminate-direction) * -140%))',
  },
  '100%': {
    transform:
      'translateX(calc(var(--progress-indeterminate-direction) * 320%))',
  },
});

// Restrict a numeric value to the provided [min, max] range.
const clamp = (value: number, min: number, max: number) =>
  Math.min(max, Math.max(min, value));

const statusFillColorMap = {
  active: '$decorativeBrandPrimaryDark',
  success: '$successIcon',
  error: '$criticalIcon',
} as const;

const INDETERMINATE_HANDOFF_HOLD_MS = 180;
const INDETERMINATE_HANDOFF_FADE_MS = 260;

/**
 * Helper for associating a busy region with a progressbar.
 *
 * Apply the returned props to the container whose content is being updated
 * (for example, the full results list region in bulk action setting).
 * This sets `aria-busy` on that region and links it to the progressbar via
 * `aria-describedby`.
 */
export const getProgressBarRegionA11yProps = (
  progressBarId: string,
  isBusy: boolean,
) => ({
  'aria-busy': isBusy,
  'aria-describedby': progressBarId,
});

interface BaseProgressBarProps {
  /**
   * HTML id forwarded to the element with role="progressbar".
   * Use this id with `getProgressBarRegionA11yProps` so a related region
   * (like a changing list container) can reference this progressbar.
   */
  id?: string;
  /** Accessible label text. Rendered visually unless hideLabel is true. */
  label: string;
  /**
   * Optional ids for supplementary descriptive content.
   * Example usage: render counts/status text in `helperInfo` with an id and pass
   * that id here so screen readers announce those details with the progressbar.
   */
  ariaDescribedBy?: string;
  hideLabel?: boolean;
  /** Minimum value for determinate mode (default: 0) */
  min?: number;
  /** Maximum value for determinate mode (default: 100) */
  max?: number;
  /** Status of the loading operation */
  status?: 'active' | 'success' | 'error';
  height?: string | number;
  /** Helper information to display below the progress bar */
  helperInfo?: React.ReactNode;
}

export interface DeterminateProgressBarProps extends BaseProgressBarProps {
  mode: 'determinate';
  /** Current value for determinate mode */
  value: number;
}

export interface IndeterminateProgressBarProps extends BaseProgressBarProps {
  /** indeterminate mean unspecified completion percentage - animation will run until a value is set */
  mode: 'indeterminate';
  value?: never;
}

export type ProgressBarProps =
  | DeterminateProgressBarProps
  | IndeterminateProgressBarProps;

export const ProgressBar = ({
  id,
  mode,
  label,
  ariaDescribedBy,
  hideLabel = false,
  value,
  min = 0,
  max = 100,
  status = 'active',
  height,
  helperInfo,
}: ProgressBarProps) => {
  const labelId = React.useId();
  const previousModeRef = React.useRef<ProgressBarProps['mode']>(mode);
  const overlayHideTimerRef = React.useRef<number | null>(null);
  const [showIndeterminateOverlay, setShowIndeterminateOverlay] =
    React.useState(mode === 'indeterminate');
  const safeMax = max > min ? max : min + 1;
  const isSemanticallyIndeterminate = mode === 'indeterminate';

  React.useEffect(() => {
    if (overlayHideTimerRef.current !== null) {
      window.clearTimeout(overlayHideTimerRef.current);
      overlayHideTimerRef.current = null;
    }

    if (mode === 'indeterminate') {
      setShowIndeterminateOverlay(true);
      previousModeRef.current = mode;
      return;
    }

    const isSwitchingFromIndeterminate =
      previousModeRef.current === 'indeterminate';
    if (isSwitchingFromIndeterminate && status === 'active') {
      // For smoother indeterminate -> determinate handoff, keep the moving
      // segment during a brief hold, then fade it out before unmounting.
      setShowIndeterminateOverlay(true);
      overlayHideTimerRef.current = window.setTimeout(() => {
        setShowIndeterminateOverlay(false);
        overlayHideTimerRef.current = null;
      }, INDETERMINATE_HANDOFF_HOLD_MS + INDETERMINATE_HANDOFF_FADE_MS);
    } else {
      setShowIndeterminateOverlay(false);
    }

    previousModeRef.current = mode;
  }, [mode, status]);

  React.useEffect(
    () => () => {
      if (overlayHideTimerRef.current !== null) {
        window.clearTimeout(overlayHideTimerRef.current);
      }
    },
    [],
  );
  // Keep determinate progress within bounds so width/ARIA values stay valid.
  const determinateValue = mode === 'determinate' ? value : min;
  // Clamp prevents negative or >100% visual widths when callers pass out-of-range values.
  const clampedValue = clamp(determinateValue, min, safeMax);
  const determinatePercent = ((clampedValue - min) / (safeMax - min)) * 100;
  const fillColor = statusFillColorMap[status];
  const shouldRenderIndeterminateOverlay =
    status === 'active' &&
    (isSemanticallyIndeterminate || showIndeterminateOverlay);

  return (
    <StackLayout
      spacing="$threeQuarters"
      css={{
        width: '100%',
      }}
    >
      {!hideLabel ? (
        <Box>
          <ParagraphText id={labelId}>{label}</ParagraphText>
        </Box>
      ) : (
        <VisuallyHidden id={labelId}>{label}</VisuallyHidden>
      )}
      <Box
        id={id}
        role="progressbar"
        aria-labelledby={labelId}
        aria-describedby={ariaDescribedBy}
        aria-valuemin={isSemanticallyIndeterminate ? undefined : min}
        aria-valuemax={isSemanticallyIndeterminate ? undefined : safeMax}
        aria-valuenow={isSemanticallyIndeterminate ? undefined : clampedValue}
        css={{
          position: 'relative',
          width: '100%',
          height: height ?? '1.25rem',
          backgroundColor: '$surfaceBackgroundSecondary',
          border: '$borderWidths$thin solid $onSurfaceBorderSubdued',
          borderRadius: '$small',
          overflow: 'hidden',
        }}
      >
        {!isSemanticallyIndeterminate && (
          <Box
            css={{
              width: `${determinatePercent}%`,
              height: '100%',
              borderRadius: '$small',
              backgroundColor: fillColor,
              transition:
                'width $transitions$defaultTime ease-out, opacity $transitions$defaultTime ease-out',
              opacity: showIndeterminateOverlay ? 0.75 : 1,
            }}
          />
        )}
        {shouldRenderIndeterminateOverlay && (
          <Box
            css={{
              position: 'absolute',
              insetBlockStart: 0,
              insetInlineStart: 0,
              width: '35%',
              height: '100%',
              borderRadius: '$small',
              backgroundColor: fillColor,
              // Constant-speed animation follows standard indeterminate progress behavior.
              animation: `${indeterminateAnimation} 2s linear infinite`,
              '--progress-indeterminate-direction': 1,
              '&:dir(rtl)': {
                '--progress-indeterminate-direction': -1,
              },
              opacity: isSemanticallyIndeterminate ? 1 : 0,
              transition: `opacity ${INDETERMINATE_HANDOFF_FADE_MS}ms ease-out`,
              transitionDelay: isSemanticallyIndeterminate
                ? '0ms'
                : `${INDETERMINATE_HANDOFF_HOLD_MS}ms`,
              '@media (prefers-reduced-motion: reduce)': {
                animation: 'none',
                transform: 'translateX(0)',
                width: '50%',
              },
            }}
          />
        )}
      </Box>
      {helperInfo}
    </StackLayout>
  );
};
